import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { LandingPage } from './pages/LandingPage';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { Dashboard } from './pages/Dashboard';
import { Credentials } from './pages/Credentials';
import { Certificates } from './pages/Certificates';
import { Signatures } from './pages/Signatures';
import { Members } from './pages/Members';
import { Groups } from './pages/Groups';
import { AuditLog } from './pages/AuditLog';
import { Settings } from './pages/Settings';
import Billing from './pages/Billing';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import FAQ from './pages/FAQ';
import { Organizations } from './pages/Organizations';
import { JoinOrganization, JoinConfirm } from './pages/JoinOrganization';
import { PENDING_INVITE_TOKEN_KEY } from './utils/constants';

// Se a pessoa chegou por um link de convite antes de logar, volta para ele após login/cadastro
function getPostLoginPath() {
  try {
    const pendingInvite = sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY);
    return pendingInvite ? `/join/${encodeURIComponent(pendingInvite)}` : '/dashboard';
  } catch {
    return '/dashboard';
  }
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-bg-root)', color: 'var(--color-text-primary)' }}>Carregando...</div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-bg-root)', color: 'var(--color-text-primary)' }}>Carregando...</div>;
  }
  
  return !isAuthenticated ? children : <Navigate to={getPostLoginPath()} />;
}

export default function App() {
  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      
      {/* Legal Routes */}
      <Route path="/terms" element={<TermsOfUse />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/faq" element={<FAQ />} />

      {/* Link de convite (trata login/cadastro dentro da própria página) */}
      <Route path="/join/confirm" element={<JoinConfirm />} />
      <Route path="/join/:token" element={<JoinOrganization />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/credentials" element={<PrivateRoute><Credentials /></PrivateRoute>} />
      <Route path="/certificates" element={<PrivateRoute><Certificates /></PrivateRoute>} />
      <Route path="/signatures" element={<PrivateRoute><Signatures /></PrivateRoute>} />
      <Route path="/members"element={<PrivateRoute><Members /></PrivateRoute>} />
      <Route path="/groups" element={<PrivateRoute><Groups /></PrivateRoute>} />
      <Route path="/audit-logs" element={<PrivateRoute><AuditLog /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
      <Route path="/billing" element={<PrivateRoute><Billing /></PrivateRoute>} />
      <Route path="/organizations" element={<PrivateRoute><Organizations /></PrivateRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}
